import { DateTime } from 'luxon'
import { AxiosError } from 'axios'

import { IAppConsentsModel, IAppConsentsModelToSend } from 'service/SettingsService/SettingsServiceModels'
import { HttpClient } from 'common/HttpClient'
import { Environment } from 'enviroment/AppSettings'
import { FormStateModel } from 'store/FormSlice/FormSliceModel'
import { SessionStorage } from 'common/CookieStorage'
import { STORAGEKEYS } from 'enviroment/Keys'
import { AdditionalApplicationDataProps } from 'components/Form/AppForm/AppFormProps'
import { 
   ApplicantDataFieldNames, IAdditionalRetentionData, IExtendedInfoFieldsToModel, 
   IFormSubmitionIncomeDataModel, IFormSubmitionModel, ILoanAmountLimitationsModel, 
   IMObywatelSubmitionModel, IRetentionSubmitionModel, ISubmitFormResponse, IVideoVerificationAdditionalData, 
   IVideoVerificationSubmitionModel, UNSPECIFIED_INCOME_TYPE 
} from './FormSubmitionModels'


// ================================= HELPERS =================================
/**
 * Mapuje zgody z aplikacji do modelu wymaganego przez Backend
 * 
 * @param appConsents - Model zgód używany w stanie aplikacji
 * @returns { IAppConsentsModelToSend[] } - Tablica zmapowanych na model z Backendu zgód
 */
const mapConsentsToModel = (appConsents: IAppConsentsModel[]): IAppConsentsModelToSend[] => {
   if(appConsents && appConsents.length > 0){
     const appConsentsToSend: IAppConsentsModelToSend[] = [];
 
     for (const appConsent of appConsents) {
       appConsentsToSend.push({
         id: appConsent.consentId,
         text: appConsent.text,
         isConsent: appConsent.isChecked
       })
     }
     return appConsentsToSend
   }
 
   else return []
}


/**
 * Tworzy obiekt z konkrentym typem dochodu, akceptowanym przez Backend.
 * 
 * @param incomePerMonthFromState - Dochody miesięczne
 * @returns { IFormSubmitionIncomeDataModel[] } - Tablica z obiektem reprezentującym typ dochodu akceptowany przez Backend
*/
const mapIncomeDateToModel = (incomePerMonthFromState: string): IFormSubmitionIncomeDataModel[]  => {

   const incomePerMonth = parseFloat(incomePerMonthFromState as string) ?? 0

   return [{
      type: UNSPECIFIED_INCOME_TYPE,
      incomePerMonth: incomePerMonth,
   }]
}

/**
 * Mapuje dane z ostatniego etapu formularza do modelu z Backendu.
 * W zależności od tego czy rządana kwota pożyczki jest większa czy mniejsza od zadanego limitu, zwracamy wartości wszystkich pól, lub tylko wybranych 
 * 
 * @param loanLimitations - Limity pożyczki, względem których zwracamy inną ilość pól
 * @param formState - Stan formularza
 * @returns { IFormSubmitionIncomeDataModel[] } - Tablica z obiektem reprezentującym typ dochodu akceptowany przez Backend
*/
const mapExtendedInfoFieldsToModel = (loanLimitations: ILoanAmountLimitationsModel, formState: FormStateModel): IExtendedInfoFieldsToModel => {
   let additionalFields = {}
   
   if(loanLimitations.amountToPay >= loanLimitations.minimalLoanAmountToDisplayAdditionalFields){
      additionalFields = {} // W tym miejscu przywrócić logikę PÓL ZALEŻNYCH OD KWOTY POŻYCZKI
   }

   return {
      [ApplicantDataFieldNames.personalId]: formState.Pesel.value as string,
      [ApplicantDataFieldNames.personalIdNumber]: formState.PersonalId.value as string,
      [ApplicantDataFieldNames.personalIdRelease]: DateTime.fromFormat(formState.PersonalIdRelease.value as string, "dd/MM/yyyy").toUTC().toISO() as string,
      [ApplicantDataFieldNames.personalIdValidity]: DateTime.fromFormat(formState.PersonalIdRelease.value as string, "dd/MM/yyyy").plus({ years: 10 }).toUTC().toISO() as string,
      ...additionalFields
   }
}

export class FormSubmitionService {
   // =============== URLS ======================
   private readonly _client: HttpClient;
   private readonly _submitBasicUrlPath = '/application/submitBasic'
   private readonly _submitBasicRetentionUrlPath = '/application/submitBasicRetention'
   private readonly _submitBasicMObywatelUrlPath = '/application/submitBasicMob'
   private readonly _submitBasicVideoVerificationUrlPath = '/application/submitBasicVideo'

   
   public constructor() {
     this._client = new HttpClient(Environment.apiUrl)
   }

   /**
    * Główna funkcja mapująca stan Frontendu na model używany w requeście do składania wniosku o pożyczkę
    * @param loanLimitations - Limity pożyczki, względem których zwracamy inną ilość pól
    * @param formState - Aktualny stan formularza
    * @param appConsents - Aktualne zgody z aplikacji
    * @param payingBillCompany - Nazwa brokera
    * @returns 
    */
   public mapFieldsToSubmitionModel(loanLimitations: ILoanAmountLimitationsModel, formState: FormStateModel, appConsents: IAppConsentsModel[], payingBillCompany: string): IFormSubmitionModel {
      const sessionStorage = new SessionStorage()
      const appConsentsToSend: IAppConsentsModelToSend[]  = mapConsentsToModel(appConsents)

      const formSubmition: IFormSubmitionModel = {
         // Dane z formularza podstawowego
         [ApplicantDataFieldNames.name] : formState.FirstName.value as string,
         [ApplicantDataFieldNames.surname] : formState.Surname.value as string,
         [ApplicantDataFieldNames.email] : formState.Email.value as string,
         [ApplicantDataFieldNames.phoneNumber] : (formState.PhoneNumber.value as string).replace(Environment.phoneNumberPrefix, '').replace(/[^\d]/g, ''),

         // Dana z formularza adresowego
         [ApplicantDataFieldNames.city]: formState.City.value as string,
         [ApplicantDataFieldNames.postalCode]: (formState.PostalCode.value as string).replace('-', ''),
         [ApplicantDataFieldNames.street]: formState.Street.value as string,
         [ApplicantDataFieldNames.houseNumber] : formState.HouseNumber.value as string,
         [ApplicantDataFieldNames.flatNumber] : formState.ApartamentNumber.value as string,
            
         // Dane z formularza rozszerzonego
         ...mapExtendedInfoFieldsToModel(loanLimitations, formState),
         consents: appConsentsToSend,
         payingBillCompany: payingBillCompany,
         jsc: sessionStorage.get(STORAGEKEYS.JSC) ?? '',
         hdim: sessionStorage.get(STORAGEKEYS.HDIM) ?? '',
      }

     
     return formSubmition
   }


   public mapFieldsToRetentionSubmitionModel(appConsents: IAppConsentsModel[], additionalRetentionData: IAdditionalRetentionData): IRetentionSubmitionModel {
      const appConsentsToSend: IAppConsentsModelToSend[]  = mapConsentsToModel(appConsents)

      const retentionSubmition: IRetentionSubmitionModel = {
         consents: appConsentsToSend,
         payingBillCompany: additionalRetentionData.payingBillCompany,
         jsc: additionalRetentionData.jsc,
         hdim: additionalRetentionData.hdim
      }

      return retentionSubmition
   }


   public mapFieldsToMObywatelSubmitionModel(formState: FormStateModel, appConsents: IAppConsentsModel[], additionalApplicationData: AdditionalApplicationDataProps): IMObywatelSubmitionModel {
      const sessionStorage = new SessionStorage()
      const appConsentsToSend: IAppConsentsModelToSend[]  = mapConsentsToModel(appConsents)

      const {amountToPay, billingCompany, minimalLoanAmountToDisplayAdditionalFields } = additionalApplicationData

      const formSubmition: IMObywatelSubmitionModel = {
         [ApplicantDataFieldNames.email] : formState.Email.value as string,
         [ApplicantDataFieldNames.phoneNumber] : (formState.PhoneNumber.value as string).replace(Environment.phoneNumberPrefix, '').replace(/[^\d]/g, ''),
         
         consents: appConsentsToSend,
         payingBillCompany: billingCompany,
         jsc: sessionStorage.get(STORAGEKEYS.JSC) ?? '',
         hdim: sessionStorage.get(STORAGEKEYS.HDIM) ?? '',
      }

      return formSubmition
   }

   public mapFieldsToVideoVerificationModel(formState: FormStateModel, appConsents: IAppConsentsModel[], additionalVideoVerificationData: IVideoVerificationAdditionalData): IVideoVerificationSubmitionModel {
      const sessionStorage = new SessionStorage()
      const appConsentsToSend: IAppConsentsModelToSend[]  = mapConsentsToModel(appConsents)

      const formSubmition: IVideoVerificationSubmitionModel = {
         // Dane z formularza podstawowego
         [ApplicantDataFieldNames.email] : formState.Email.value as string,
         [ApplicantDataFieldNames.phoneNumber] : (formState.PhoneNumber.value as string).replace(Environment.phoneNumberPrefix, '').replace(/[^\d]/g, ''),

         // Dana z formularza adresowego
         [ApplicantDataFieldNames.city]: formState.City.value as string,
         [ApplicantDataFieldNames.postalCode]: (formState.PostalCode.value as string).replace('-', ''),
         [ApplicantDataFieldNames.street]: formState.Street.value as string,
         [ApplicantDataFieldNames.houseNumber] : formState.HouseNumber.value as string,
         [ApplicantDataFieldNames.flatNumber] : formState.ApartamentNumber.value as string,
            

         consents: appConsentsToSend,
         sessionId: additionalVideoVerificationData.sessionId,
         payingBillCompany: additionalVideoVerificationData.payingBillCompany,
         jsc: sessionStorage.get(STORAGEKEYS.JSC) ?? '',
         hdim: sessionStorage.get(STORAGEKEYS.HDIM) ?? '',
      }

     
     return formSubmition
   }

   public async submitBasic(application: IFormSubmitionModel): Promise<ISubmitFormResponse> {
      try {
         return (await this._client.post<ISubmitFormResponse, IFormSubmitionModel>(
           this._submitBasicUrlPath,
           application,
           true
         )).data
      }
      catch (e) {
         throw new AxiosError(`Nie udało się poprawnie złożyć wniosku`)
      }

   }
   public async submitBasicRetention(application: IRetentionSubmitionModel): Promise<ISubmitFormResponse> {
      try {
         return (await this._client.post<ISubmitFormResponse, IRetentionSubmitionModel>(
           this._submitBasicRetentionUrlPath,
           application,
           true
         )).data
      }
      catch (e) {
         throw new AxiosError(`Nie udało się poprawnie złożyć wniosku retencyjnego`)
      }
   }
   public async submitBasicMObywatel(application: IMObywatelSubmitionModel): Promise<ISubmitFormResponse> {
      try {
         return (await this._client.post<ISubmitFormResponse, IMObywatelSubmitionModel>(
           this._submitBasicMObywatelUrlPath,
           application,
           true
         )).data
      }
      catch (e) {
         throw new AxiosError(`Nie udało się poprawnie złożyć wniosku mObywatel`)
      }
   }
   public async submitBasicVideoVerification(application: IVideoVerificationSubmitionModel): Promise<ISubmitFormResponse> {
      try {
         return (await this._client.post<ISubmitFormResponse, IVideoVerificationSubmitionModel>(
           this._submitBasicVideoVerificationUrlPath,
           application,
           true
         )).data
      }
      catch (e) {
         throw new AxiosError(`Nie udało się poprawnie złożyć wniosku`)
      }
   }
}